import { StandardProps } from "@/components/componentMap";
import { useMemo } from "react";
import { useVariant } from "@/hooks/useVariant/useVariant";
import {
  ensureIsActive,
  ensureIsActiveVariant,
} from "@/utils/product/ensureIsActive";
import AvailableServices from "@/components/PostAddToBasketDrawer/components/AvailableServices.tsx";
import { getBasketMode } from "@/components/PostAddToBasketDrawer/basketMode";
import { getScheme } from "@/components/PostAddToBasketDrawer/scheme/schemeDecisioner.ts";
import { ProtectPlusComplianceDrawerUI } from "@/components/PostAddToBasketDrawer/ProtectPlusComplianceDrawerUI.tsx";
import { useSharedServices } from "@/utils/services/useSharedServices";

type Props = Partial<StandardProps> &
  Pick<StandardProps, "product" | "runtimeConfig" | "flags">;

export function PostAddToBasketDrawer({
  configProps,
  product,
  runtimeConfig,
  flags,
}: Props) {
  const { variant, isSelected } = useVariant(product);
  const { availableServices, actions } = useSharedServices({ product, flags });

  const { services = [] } = variant;
  const { brand } = product;

  const mode = getBasketMode({
    isShipFromStore: variant.availability?.shipFromStore === true,
    expressCheckoutEnabled: configProps?.expressCheckoutEnabled === true,
    brandName: brand?.name,
  });
  const mandatoryServicesOverride = flags.mandatoryServicesOverride.value
    .toString()
    .split(",");

  const deliveryAndWarrantyServices = useMemo(
    () =>
      services.filter(({ __typename }) =>
        ["DeliveryService", "ExtendedWarranty"].includes(__typename),
      ),
    [services],
  );

  const guarantees = services.filter(
    ({ __typename }) => __typename === "IncludedGuarantee",
  );

  const warrantyServices = services.filter(
    ({ __typename }) => __typename === "ExtendedWarranty",
  );

  const activeProduct = ensureIsActive(product);

  const activeVariant = ensureIsActiveVariant(variant);

  if (!activeProduct || !activeVariant) return null;

  const isOutOfStock = !variant.availability?.availableToOrder;

  const protectPlusScheme = getScheme(product);

  return (
    <>
      <AvailableServices
        variantServices={warrantyServices}
        protectPlusScheme={protectPlusScheme}
        mode={mode}
      />
      <ProtectPlusComplianceDrawerUI
        basketMode={mode}
        title={variant.title}
        variantServices={deliveryAndWarrantyServices}
        basketBaseUrl={runtimeConfig.basketApiBaseUrl}
        productId={activeProduct?.aliases.pimProductId}
        skuId={activeVariant?.aliases.skuId}
        guarantees={guarantees}
        protectPlusScheme={protectPlusScheme}
        hideFooter={isOutOfStock || !isSelected}
        mandatoryServicesOverride={mandatoryServicesOverride}
        availableServices={availableServices}
        actions={actions}
      />
    </>
  );
}
